import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { HandCoins, Upload, Send, Download, Search, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDateTime = (s) => (s
  ? new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—');

const CompensationManagement = () => {
  const [users, setUsers] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [letters, setLetters] = useState([]);

  const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchLetters = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/compensation/all`, authConfig());
      setLetters(res.data || []);
    } catch (e) {
      toast.error('Failed to load uploaded letters');
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/users`, authConfig());
        setUsers(res.data || []);
      } catch (e) {
        toast.error('Failed to load employees');
      }
    })();
    fetchLetters();
  }, [fetchLetters]);

  const filteredUsers = useMemo(() => {
    const q = employeeSearch.trim().toLowerCase();
    const list = [...users].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (!q) return list;
    return list.filter((u) =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.employee_id || '').toLowerCase().includes(q));
  }, [users, employeeSearch]);

  const handleSubmit = async () => {
    if (!selectedEmployeeId) {
      toast.error('Please select an employee');
      return;
    }
    if (!file) {
      toast.error('Please choose a PDF letter to upload');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('employee_id', selectedEmployeeId);
      formData.append('file', file);
      const res = await axios.post(`${API}/compensation/upload`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data.message);
      setFile(null);
      setSelectedEmployeeId('');
      document.getElementById('compensation-file-input').value = '';
      fetchLetters();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload compensation letter');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (letter) => {
    try {
      const res = await axios.get(`${API}/compensation/${letter.id}/download`, {
        ...authConfig(), responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = letter.original_filename || 'Compensation_Letter.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download the PDF');
    }
  };

  return (
    <div className="space-y-4" data-testid="compensation-management">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><HandCoins className="w-5 h-5 text-emerald-600" /> Upload Compensation Letter</CardTitle>
          <CardDescription>Select an employee, upload their compensation letter PDF, and submit — it lands in their Compensation Portal and is emailed to them.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="text-sm font-medium text-gray-700">Employee</label>
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full mt-1 justify-start font-normal" data-testid="compensation-employee-select">
                    {selectedEmployeeId
                      ? (() => { const u = users.find((x) => x.id === selectedEmployeeId); return u ? `${u.name}${u.employee_id ? ` (${u.employee_id})` : ''}` : 'Select employee'; })()
                      : <span className="text-gray-400">Select employee</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                  <div className="p-2 border-b">
                    <div className="flex items-center gap-2">
                      <Search className="w-4 h-4 text-gray-400" />
                      <Input
                        autoFocus
                        value={employeeSearch}
                        onChange={(e) => setEmployeeSearch(e.target.value)}
                        placeholder="Type name, email or ID…"
                        className="border-0 shadow-none focus-visible:ring-0 h-8"
                        data-testid="compensation-employee-search"
                      />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {filteredUsers.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => { setSelectedEmployeeId(u.id); setPickerOpen(false); setEmployeeSearch(''); }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${selectedEmployeeId === u.id ? 'bg-blue-50 text-blue-700' : ''}`}
                        data-testid={`compensation-emp-option-${u.employee_id || u.id}`}
                      >
                        {u.name} {u.employee_id ? `(${u.employee_id})` : ''}
                      </button>
                    ))}
                    {filteredUsers.length === 0 && <div className="p-3 text-sm text-gray-500">No employees match.</div>}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Compensation Letter (PDF)</label>
              <Input
                id="compensation-file-input"
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1"
                data-testid="compensation-file-input"
              />
            </div>
            <div>
              <Button onClick={handleSubmit} disabled={submitting} className="w-full" data-testid="compensation-submit-btn">
                {submitting ? <Upload className="w-4 h-4 mr-2 animate-bounce" /> : <Send className="w-4 h-4 mr-2" />}
                {submitting ? 'Uploading…' : 'Submit & Send'}
              </Button>
            </div>
          </div>
          {file && <p className="text-xs text-gray-500">Selected: {file.name} ({Math.round(file.size / 1024)} KB)</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Uploaded Letters ({letters.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {letters.length === 0 ? (
            <div className="text-center py-8 text-gray-500" data-testid="compensation-admin-empty">
              <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No compensation letters uploaded yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Letter</TableHead>
                    <TableHead>Uploaded On</TableHead>
                    <TableHead>Uploaded By</TableHead>
                    <TableHead className="text-right">Download</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {letters.map((l) => (
                    <TableRow key={l.id} data-testid={`compensation-admin-row-${l.id}`}>
                      <TableCell>
                        <div className="font-medium">{l.employee_name}</div>
                        <div className="text-xs text-gray-400">{l.employee_code || l.employee_email}</div>
                      </TableCell>
                      <TableCell>{l.original_filename}</TableCell>
                      <TableCell>{fmtDateTime(l.uploaded_at)}</TableCell>
                      <TableCell>{l.uploaded_by_name || l.uploaded_by}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => handleDownload(l)} data-testid={`compensation-admin-download-${l.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CompensationManagement;
